import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Select,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useForm, Controller } from 'react-hook-form';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { validateUrlField } from '../../lib/validateUrlField';

interface SendPushNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: {
    id: string;
    deviceToken: string;
    platform: string;
    deviceModel: string | null;
  };
  userDrn: string;
}

interface PushNotificationFormData {
  title: string;
  body: string;
  url?: string;
  sound?: string;
  priority: 'high' | 'normal';
  badgeCount?: number;
  imageUrl?: string;
}

export function SendPushNotificationModal({
  isOpen,
  onClose,
  device,
  userDrn,
}: SendPushNotificationModalProps) {
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<PushNotificationFormData>({
    defaultValues: {
      title: 'Test Notification',
      body: 'This is a test push notification from Duke Admin',
      url: '',
      sound: 'default',
      priority: 'high',
      badgeCount: undefined,
      imageUrl: '',
    },
  });

  const sendNotificationMutation = trpcAdmin.students.sendTestPushNotification.useMutation({
    onSuccess: (data) => {
      toast({
        status: 'success',
        title: t`Notification sent successfully`,
        description: t`Notification ID: ${data.notificationId}`,
      });
      reset();
      onClose();
    },
    onError: (error) => {
      toast({
        status: 'error',
        title: t`Failed to send notification`,
        description: error.message,
      });
    },
  });

  const onSubmit = (data: PushNotificationFormData) => {
    const platformLower = device.platform.toLowerCase();
    let platform: 'ios' | 'android' | 'web';

    if (platformLower === 'ios') {
      platform = 'ios';
    } else if (platformLower === 'android') {
      platform = 'android';
    } else if (platformLower === 'web') {
      platform = 'web';
    } else {
      toast({
        status: 'error',
        title: t`Invalid platform`,
        description: t`Device platform ${device.platform} is not supported`,
      });
      return;
    }

    sendNotificationMutation.mutate({
      deviceId: device.id,
      deviceToken: device.deviceToken,
      platform,
      userDrn,
      title: data.title,
      body: data.body,
      url: data.url != null && data.url !== '' ? data.url : undefined,
      sound: data.sound != null && data.sound !== '' ? data.sound : undefined,
      priority: data.priority,
      badgeCount: data.badgeCount,
      imageUrl: data.imageUrl != null && data.imageUrl !== '' ? data.imageUrl : undefined,
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>
          <Trans>Send Test Push Notification</Trans>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isInvalid={errors.title != null} isRequired>
              <FormLabel>
                <Trans>Title</Trans>
              </FormLabel>
              <Input
                {...register('title', {
                  required: t`Title is required`,
                  minLength: { value: 1, message: t`Title must be at least 1 character` },
                  maxLength: { value: 65, message: t`Title must be at most 65 characters` },
                })}
                placeholder={t`Notification title (1-65 characters)`}
              />
              <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.body != null} isRequired>
              <FormLabel>
                <Trans>Message</Trans>
              </FormLabel>
              <Textarea
                {...register('body', {
                  required: t`Message is required`,
                  minLength: { value: 1, message: t`Message must be at least 1 character` },
                  maxLength: { value: 240, message: t`Message must be at most 240 characters` },
                })}
                placeholder={t`Notification message (1-240 characters)`}
                rows={4}
              />
              <FormErrorMessage>{errors.body?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.url != null}>
              <FormLabel>
                <Trans>Deep Link URL (Optional)</Trans>
              </FormLabel>
              <Input
                {...register('url', {
                  validate: validateUrlField,
                })}
                placeholder={t`https://app.duke.co/...`}
              />
              <FormErrorMessage>{errors.url?.message}</FormErrorMessage>
            </FormControl>

            <HStack spacing={4}>
              <FormControl>
                <FormLabel>
                  <Trans>Sound</Trans>
                </FormLabel>
                <Input {...register('sound')} placeholder={t`default`} />
              </FormControl>

              <FormControl>
                <FormLabel>
                  <Trans>Priority</Trans>
                </FormLabel>
                <Select {...register('priority')}>
                  <option value="high">
                    <Trans>High</Trans>
                  </option>
                  <option value="normal">
                    <Trans>Normal</Trans>
                  </option>
                </Select>
              </FormControl>
            </HStack>

            <FormControl isInvalid={errors.badgeCount != null}>
              <FormLabel>
                <Trans>Badge Count (Optional)</Trans>
              </FormLabel>
              <Controller
                name="badgeCount"
                control={control}
                rules={{
                  min: { value: 1, message: t`Badge count must be at least 1` },
                }}
                render={({ field }) => (
                  <NumberInput
                    min={1}
                    value={field.value ?? ''}
                    onChange={(_, valueAsNumber) => {
                      field.onChange(Number.isNaN(valueAsNumber) ? undefined : valueAsNumber);
                    }}
                  >
                    <NumberInputField placeholder={t`Badge count`} />
                  </NumberInput>
                )}
              />
              <FormErrorMessage>{errors.badgeCount?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.imageUrl != null}>
              <FormLabel>
                <Trans>Image URL (Optional)</Trans>
              </FormLabel>
              <Input
                {...register('imageUrl', {
                  validate: validateUrlField,
                })}
                placeholder={t`https://...`}
              />
              <FormErrorMessage>{errors.imageUrl?.message}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={handleClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
              <Trans>Send Notification</Trans>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
